import { memo, useEffect, useState } from "react";
import { ButtonComponent } from "@zenith/components";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { ModalComponent } from "@zenith/components";
import { useSendMoneyContext } from "@zenith/context";
import {
  DEFAULT_CHANNEL,
  DEFAULT_CURRENCY,
  DISCARD_TRANSFER_BUTTON,
  GENERIC_ERROR_CODE,
  GENERIC_ERROR_MESSAGE,
  LIMIT_UNAUTHORIZED_AMOUNT_MODAL_BODY,
  LOCKED_PASSCODE_ERROR_CODE,
  PIN_CODE_REQUIRED_FOUR_DIGITS_MESSAGE,
  PIN_CODE_REQUIRED_MESSAGE,
  SUCCESS_API_CODE,
  SWITCH_UNAVAILABLE_ERROR_CODE,
  SWITCH_UNAVAILABLE_MESSAGE,
  TIMEOUT_ERROR_CODE,
  TIMEOUT_ERROR_MODAL_TITLE,
  TRANSACTION_PIN_LOCKED_ERROR_MESSAGE,
  TRANSACTION_PIN_MAXIMUM_ATTEMPTS_ERROR_MESSAGE,
  TRANSACTION_PIN_PAGE_LABEL,
  TRANSACTION_PIN_PAGE_SUBTITLE,
  TRANSACTION_PIN_PAGE_TITLE,
  TRANSACTIONS_PIN_LENGTH,
  TRANSFER_TIMEOUT_ERROR_MODAL_BODY,
  TRY_AGAIN_BUTTON,
  UNAUTHORIZED_AMOUNT_ERROR_MODAL_TITLE,
  UNAUTHORIZED_BALANCE_LIMIT_ERROR_CODE,
  WRONG_PASSCODE_ERROR_CODE,
  WRONG_PIN_FIRST_TRY,
  WRONG_PIN_SECOND_TRY,
} from "@zenith/data/constants";
import { PaymentsPinPageComponent } from "@zenith/features/Payments/components";
import {
  DASHBOARD,
  SEND_MONEY,
  SEND_MONEY_AUTHENTICATION,
  SEND_MONEY_OVERVIEW,
  SEND_MONEY_SUCCESS,
} from "@zenith/routes/paths";
import {
  ApiResponseDTOOperationDTO,
  OperationDTO,
  OperationOutBankTransferRecord,
  OperationsApiService,
} from "@zenith/services/transactions";
import {
  ApiResponseDTOAuthenticationDTO,
  ApiResponseDTOEncryptionDTO,
  ApiResponseDTOTransactionPinDetailDTO,
  AuthenticationApiService,
  UtilityApiService,
  WalletApiService,
} from "@zenith/services/user";
import { FormFieldErrorType } from "@zenith/types";
import { encrypt, getApiErrorMessage } from "@zenith/utils";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";

const NUMBER_OF_ATTEMPTS = 2;

// Transfer type constants
const TRANSFER_TYPES = {
  WALLET: "wallet",
  BANK: "bank",
} as const;

const PAYMENT_METHODS = {
  OUT_WALLET_TRANSFER: "OUT_WALLET_TRANSFER",
  OUT_BANK_TRANSFER: "OUT_BANK_TRANSFER",
} as const;

// Wallet account validation
const WALLET_ACCOUNT_PREFIX = "967";
const WALLET_ACCOUNT_LENGTH = 10;

const isWalletAccount = (accountNumber: string): boolean => {
  return (
    accountNumber.length === WALLET_ACCOUNT_LENGTH &&
    accountNumber.startsWith(WALLET_ACCOUNT_PREFIX)
  );
};

/* eslint-disable-next-line */
export interface SendMoneyTransactionPinPageProps {}

const SendMoneyTransactionPinPage = memo(function SendMoneyTransactionPinPage(
  props: SendMoneyTransactionPinPageProps
) {
  const navigate = useNavigate();
  const location = useLocation();
  const xApiKey = import.meta.env.VITE_X_API_KEY;

  const { sendMoneyContext, setSendMoneyContext, clearSendMoneyContext } =
    useSendMoneyContext();
  const {
    amount,
    bankId,
    bankName,
    beneficiaryAccountNumber,
    beneficiaryName,
    categoryId,
    categoryName,
    description,
    feeValue,
    transferType,
  } = sendMoneyContext;

  const initialFormErrorState = {
    pin: {
      message: "",
      hasError: false,
    },
  };

  const [formData, setFormData] = useState<{ pin: string }>({ pin: "" }),
    [formErrors, setFormErrors] = useState<{ pin: FormFieldErrorType }>(
      initialFormErrorState
    ),
    [newPin, setNewPin] = useState<Array<number | string>>(
      new Array(TRANSACTIONS_PIN_LENGTH).fill("")
    );

  const [showAccountLockedModal, setShowAccountLockedModal] =
    useState<boolean>(false);
  const [showBalanceOrLimitError, setShowBalanceOrLimitError] =
    useState<boolean>(false);
  const [showTransactionTimeoutError, setShowTransactionTimeoutError] =
    useState<boolean>(false);
  const [is2FAEnabled, setIs2FAEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const BalanceLimitErrorModal = ModalComponent;
  const TransactionTimeoutErrorModal = ModalComponent;

  useEffect(() => {
    AuthenticationApiService.getVerification2Fa()
      .then((response: ApiResponseDTOAuthenticationDTO) => {
        if (
          "data" in response &&
          response.data &&
          response.internalCode === SUCCESS_API_CODE
        ) {
          setIs2FAEnabled(response.data.authentication2fa as boolean);
        }
      })
      .catch((error) => new Error(error));
  }, []);

  if (!amount || !bankName || !beneficiaryAccountNumber || !beneficiaryName) {
    return <Navigate to={SEND_MONEY} replace />;
  }

  function handleBackButton(): void {
    navigate(SEND_MONEY_OVERVIEW, {
      replace: true,
      state: {
        availableBalance: location.state?.availableBalance,
      },
    });
  }

  function handlePinValidationFailure(payload: {
    internalCode?: string;
    message?: string;
    data?: { success?: boolean; availableAttempts?: number };
  }): void {
    const { internalCode, data } = payload;

    switch (internalCode) {
      case WRONG_PASSCODE_ERROR_CODE:
        setFormErrors({
          pin: {
            message:
              data?.availableAttempts === NUMBER_OF_ATTEMPTS
                ? WRONG_PIN_FIRST_TRY
                : WRONG_PIN_SECOND_TRY,
            hasError: !data?.success,
          },
        });
        return;

      case LOCKED_PASSCODE_ERROR_CODE:
        setFormErrors({ pin: { message: "", hasError: !data?.success } });
        setShowAccountLockedModal(true);
        return;

      default:
        // Any other rejection (PIN not set, bad request, 5xx, unexpected body
        // shape): echo the backend's own message - e.g. ZENZ-USER-002's
        // "Transaction PIN has not been set" - falling back to a friendly
        // one only when there isn't a usable message to show.
        setFormErrors({
          pin: {
            message: getApiErrorMessage(payload, GENERIC_ERROR_MESSAGE),
            hasError: true,
          },
        });
    }
  }

  function validateTransactionPin(transactionPin: string): void {
    WalletApiService.validateTransactionPin({
      transactionPin,
    })
      .then((response: ApiResponseDTOTransactionPinDetailDTO) => {
        if (
          "data" in response &&
          response.data &&
          response.internalCode === SUCCESS_API_CODE
        ) {
          // Always navigate to authentication page, but pass the 2FA status and balance
          navigate(SEND_MONEY_AUTHENTICATION, {
            state: {
              nameEnquiry: location.state.nameEnquiry,
              mfaToken: response.data.xmfa,
              is2FAEnabled: is2FAEnabled,
              availableBalance: location.state?.availableBalance, // Pass balance forward
            },
          });
          return;
        }

        // The API answers 2xx with a business error (e.g. wrong PIN) - the
        // payment never started, so leave the pending state and show it.
        setIsLoading(false);
        handlePinValidationFailure(response);
      })
      .catch((error) => {
        setIsLoading(false);
        handlePinValidationFailure(error?.body ?? {});
      });
  }

  function postTransaction(xmfa: string): void {
    const isWalletTransfer = transferType === TRANSFER_TYPES.WALLET;

    // Validate wallet account number format if it's a wallet transfer
    if (isWalletTransfer && !isWalletAccount(beneficiaryAccountNumber)) {
      setFormErrors({
        pin: {
          hasError: true,
          message: "Invalid wallet account number format",
        },
      });
      setIsLoading(false);
      return;
    }

    const transferRequest: OperationOutBankTransferRecord = {
      currency: DEFAULT_CURRENCY,
      amount,
      feeValue: (feeValue as number) || location.state?.feeValue || 0,
      beneficiary: {
        accountNumber: beneficiaryAccountNumber,
        name: beneficiaryName,
        bank: {
          bankName: bankName,
          id: bankId,
        },
        nameEnquiryRef: isWalletTransfer
          ? beneficiaryAccountNumber
          : sendMoneyContext.nameEnquiryRef ||
            location.state?.nameEnquiry ||
            "",
      },
      mfaToken: xmfa,
      ...(categoryId && categoryName
        ? {
            category: {
              id: Number(categoryId),
              name: categoryName,
            },
          }
        : {}),
      ...(description ? { description } : {}),
    };
    setIsLoading(true);

    OperationsApiService.postOperationOutBankTransfer(
      DEFAULT_CHANNEL,
      transferRequest
    )
      .then((response: ApiResponseDTOOperationDTO) => {
        if (
          "data" in response &&
          response.data &&
          response.internalCode === SUCCESS_API_CODE &&
          response.data?.transactionId
        ) {
          const updatedContext = {
            ...sendMoneyContext,
            accountOwner: response.data.payerAccount?.payerName as string,
            amount: response.data.outBankTransfer?.amount as number,
            bankName: response.data.outBankTransfer?.beneficiary?.bank
              .bankName as string,
            beneficiaryAccountNumber: response.data.outBankTransfer?.beneficiary
              ?.accountNumber as string,
            beneficiaryName: response.data.outBankTransfer?.beneficiary
              ?.name as string,
            feeValue: response.data.outBankTransfer?.fee,
            sessionId: response.data.outBankTransfer?.sessionId,
            status: response.data.status,
            totalAmount: response.data.totalAmount,
            transactionDate: response.data.createdAt,
            operationId: response.data.operationId,
            transactionId: response.data.transactionId,
            paymentMethod: isWalletTransfer
              ? PAYMENT_METHODS.OUT_WALLET_TRANSFER
              : PAYMENT_METHODS.OUT_BANK_TRANSFER,
          };

          setSendMoneyContext(updatedContext);

          // The processor's real outcome isn't known synchronously — land
          // on the processing screen immediately with whatever status the
          // creation call returned, and let it poll for the truthful
          // outcome (BOAS-8207: Processor Authority).
          navigate(SEND_MONEY_SUCCESS, {
            replace: true,
            state: {
              fromPolling: true,
              transactionId: response.data.transactionId,
            },
          });
        } else {
          throw new Error(response.message || "Transaction initiation failed");
        }
      })
      .catch((error: any) => {
        setIsLoading(false);
        handleTransactionError(error);

        setSendMoneyContext({
          ...sendMoneyContext,
          transactionDate: error.body?.dateTime || new Date().toISOString(),
          errorMessage: error.body?.message || GENERIC_ERROR_MESSAGE,
          status: OperationDTO.status.DECLINED,
        });

        navigate(SEND_MONEY_SUCCESS, {
          replace: true,
        });
      });
  }

  function handleTransactionError(error: any): void {
    let errorMessage = "";
    const { internalCode } = error.body;

    switch (internalCode) {
      case UNAUTHORIZED_BALANCE_LIMIT_ERROR_CODE:
        errorMessage = LIMIT_UNAUTHORIZED_AMOUNT_MODAL_BODY;
        setShowBalanceOrLimitError(true);
        break;
      case TIMEOUT_ERROR_CODE:
        errorMessage = TRANSFER_TIMEOUT_ERROR_MODAL_BODY;
        setShowTransactionTimeoutError(true);
        break;
      case SWITCH_UNAVAILABLE_ERROR_CODE:
        errorMessage = SWITCH_UNAVAILABLE_MESSAGE;
        break;
      case GENERIC_ERROR_CODE:
      default:
        errorMessage = GENERIC_ERROR_MESSAGE;
        break;
    }
    console.error(errorMessage);
  }

  function handleSubmit(): void {
    setIsLoading(true);
    UtilityApiService.getPublicKey(DEFAULT_CHANNEL, xApiKey)
      .then((response: ApiResponseDTOEncryptionDTO) => {
        const publicKeyBase64 = response?.data?.publicKeyBase64;
        const encryptedPin =
          publicKeyBase64 && response.internalCode === SUCCESS_API_CODE
            ? encrypt(formData.pin, publicKeyBase64)
            : "";

        if (!publicKeyBase64 || !encryptedPin) {
          // Nothing can be submitted - never leave the pending state on.
          setIsLoading(false);
          setFormErrors({
            pin: { message: GENERIC_ERROR_MESSAGE, hasError: true },
          });
          return;
        }

        validateTransactionPin(encryptedPin);
      })
      .catch(() => {
        setIsLoading(false);
        setFormErrors({
          pin: { message: GENERIC_ERROR_MESSAGE, hasError: true },
        });
      });
  }

  function onPinChanged(pinEntry: number | string, index: number): void {
    const pin = [...newPin];
    pin[index] = pinEntry;
    setNewPin(pin);
    setFormData({ ...formData, pin: pin.join("") });
  }

  function onPinBlur(): void {
    if (newPin.join("") === "") {
      setFormErrors({
        pin: {
          hasError: true,
          message: PIN_CODE_REQUIRED_MESSAGE,
        },
      });
      return;
    }

    if (
      newPin.join("").length > 0 &&
      newPin.join("").length < TRANSACTIONS_PIN_LENGTH
    ) {
      setFormErrors({
        pin: {
          hasError: true,
          message: PIN_CODE_REQUIRED_FOUR_DIGITS_MESSAGE,
        },
      });
      return;
    }

    setFormErrors({
      pin: {
        hasError: false,
        message: "",
      },
    });
  }

  function isContinueButtonDisabled(): boolean {
    return formData.pin.length < TRANSACTIONS_PIN_LENGTH;
  }

  function handleModalClose(): void {
    clearSendMoneyContext();
    navigate(DASHBOARD, { replace: true });
  }

  function handleBalanceErrorModalCloseBtn() {
    setShowBalanceOrLimitError(false);
  }

  function handleTimeoutErrorModalDismiss() {
    setShowTransactionTimeoutError(false);
  }

  function handleBalanceErrorModalDiscardBtn() {
    clearSendMoneyContext();
    navigate(SEND_MONEY, { replace: true });
  }

  function handleTimeoutDiscardTransferModal() {
    clearSendMoneyContext();
    navigate(DASHBOARD, { replace: true });
  }

  return (
    <>
      <ModalComponent
        title={TRANSACTION_PIN_LOCKED_ERROR_MESSAGE}
        body={TRANSACTION_PIN_MAXIMUM_ATTEMPTS_ERROR_MESSAGE}
        showModal={showAccountLockedModal}
        onClickClose={handleModalClose}
        onClickButton={handleModalClose}
        buttonText={DISCARD_TRANSFER_BUTTON}
      />
      <BalanceLimitErrorModal
        title={UNAUTHORIZED_AMOUNT_ERROR_MODAL_TITLE}
        body={LIMIT_UNAUTHORIZED_AMOUNT_MODAL_BODY}
        showModal={showBalanceOrLimitError}
        buttonText={DISCARD_TRANSFER_BUTTON}
        onClickClose={handleBalanceErrorModalCloseBtn}
        onClickButton={handleBalanceErrorModalDiscardBtn}
      />
      <TransactionTimeoutErrorModal
        title={TIMEOUT_ERROR_MODAL_TITLE}
        body={TRANSFER_TIMEOUT_ERROR_MODAL_BODY}
        showModal={showTransactionTimeoutError}
        onClickClose={handleTimeoutErrorModalDismiss}
        onClickButton={handleTimeoutErrorModalDismiss}
        doubleOptions
        classNames="h-310"
        leftButtonText={DISCARD_TRANSFER_BUTTON}
        rightButtonText={TRY_AGAIN_BUTTON}
        onLeftButtonClick={handleTimeoutDiscardTransferModal}
        onRightButtonClick={handleTimeoutErrorModalDismiss}
      />
      <div className="mb-3 flex-col items-start justify-between">
        <ButtonComponent
          label="Back"
          onClick={handleBackButton}
          id="back-button"
          size="large"
          type="tertiary"
          iconStart={<ChevronLeft />}
        />
        <PaymentsPinPageComponent
          hasError={formErrors.pin.hasError}
          id="send-money-transaction"
          title={TRANSACTION_PIN_PAGE_TITLE}
          subtitle={TRANSACTION_PIN_PAGE_SUBTITLE}
          labelText={TRANSACTION_PIN_PAGE_LABEL}
          validationMessage={formErrors.pin.message}
          pinLength={TRANSACTIONS_PIN_LENGTH}
          newPin={newPin}
          isContinueButtonDisabled={isContinueButtonDisabled()}
          onBlur={onPinBlur}
          onContinueButtonClick={handleSubmit}
          onPinChanged={onPinChanged}
          isLoading={isLoading}
        />
      </div>
    </>
  );
});

export default SendMoneyTransactionPinPage;
